import {
  DialogCard,
  DialogFooter,
  DialogHeader,
  DialogSubtitle,
  DialogTitle,
  PrimaryButton,
  SecondaryButton,
} from '@src/components/common/dialogStyles';
import { GeneratingNameLabel } from '@src/components/common/GeneratingNameLabel';
import Modal from '@src/components/common/Modal';
import Switch from '@src/components/common/Switch';
import { ToggleGroup } from '@src/components/common/ToggleGroup';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import InfoIconAlt from '@src/svgs/InfoIconAlt';
import React, { useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { ClipFilterCheckbox } from './clipFilterCheckbox';

import type { ChapterizationOptions } from '@src/services/reel/chapterizationInstructions';

/**
 * PLAT-11099 — where a chapterization run is specified (DS2).
 *
 * The menu item opens this rather than running, because chapterizing is a
 * request *with options*, and the options are what the epic is actually about
 * (E5). The shape is the Clip Producer's — structured fields plus a free-text
 * steer, one primary action — so a creator has met this dialog before.
 *
 * The overlay is also the confirmation step (DS7). On a reel that already has
 * chapters it states what the run destroys and its action reads **Replace
 * Chapters**; a second `ConfirmModal` over it would be a third z-tier inside a
 * modal that is already inside the clip-preview modal, and would add no safety —
 * the creator has to read this and press a button that says Replace either way.
 * Unlike a separate confirm, this is the step where the run is specified, so the
 * two cannot drift apart.
 *
 * Presentation and local form state only: it returns `ChapterizationOptions` and
 * knows nothing about the request, the response or the reel.
 */

/**
 * PLAT-11154 — a row in the tick list. Chapter headings appear as non-tickable
 * separators, so the creator can see which chapter they are about to leave
 * alone: the list is the same structure as the reel, or it is a list of names
 * with no relationship to the thing being edited.
 */
export type AutoChapterListRow =
  | { kind: 'chapter'; id: string; title: string }
  | { kind: 'section'; id: string; position: number; name: string };

/** This dialog's own tooltip instance — it is above the panel's, and modal. */
const AUTO_CHAPTER_TOOLTIP_ID = 'auto-chapter-tooltip';

export type AutoChapterRunOptions = ChapterizationOptions & {
  /** Absent for a whole-reel run; a set is a subset run, which preserves (E8). */
  movableSectionIds?: ReadonlySet<string>;
};

interface AutoChapterOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onRun: (options: AutoChapterRunOptions) => void;
  /** How many Sections the run has to work with — the count field's ceiling. */
  sectionCount: number;
  /** How many chapters the reel has now; above zero, this run replaces them. */
  existingChapterCount: number;
  /**
   * The reel, for the tick list: live Sections in playback order with their
   * chapter headings between them. Disabled Sections are not here — they are
   * not in the conversation at all (PLAT-11153 S7).
   */
  rows: readonly AutoChapterListRow[];
  isRunning?: boolean;
  /** The last failure, rendered verbatim; the overlay stays open with it (DS10). */
  error?: string;
}

export const AutoChapterOverlay: React.FC<AutoChapterOverlayProps> = ({
  isOpen,
  onClose,
  onRun,
  sectionCount,
  existingChapterCount,
  rows,
  isRunning = false,
  error,
}) => {
  // Auto or a number the creator specifies — a segmented toggle rather than an
  // empty-means-auto text field, which made the default a thing you had to know
  // rather than a thing you could see (design system → Toggle Group).
  const [countMode, setCountMode] = useState<'auto' | 'specify'>('auto');
  // Held as the raw string so the field can be cleared and retyped without the
  // value snapping back to a number mid-edit.
  const [count, setCount] = useState('');
  const [guidance, setGuidance] = useState('');
  // On by default: auto-chaptering is asked for on a reel that was assembled
  // rather than sequenced, and the run is worth most when it may decide the
  // order as well as the grouping. A creator who wants their sequence kept says
  // so in one click, and the notice under the actions says what is at stake.
  const [reorder, setReorder] = useState(true);
  // Also on by default, and for the same reason one level down: a reel handed to
  // this dialog is usually a pile of clips, and a pile of clips cut from an
  // interview arrives grouped by whoever was talking. Left off, the ordinary run
  // produces a reel that plays as one person at length — which is the thing this
  // setting exists to prevent, discovered only by someone who found the switch.
  //
  // Only ever sent alongside a reorder — there is nothing to vary in an order
  // that is being kept. Held rather than derived so the choice survives a
  // detour through "Keep Current Order" and back.
  const [varySpeakers, setVarySpeakers] = useState(true);
  // PLAT-11154 — the tick list lives and dies with the run (S1). A selection
  // model on the Section rows would be persistent, visible state on a surface
  // where it means nothing the rest of the time.
  const [choosing, setChoosing] = useState(false);
  const [ticked, setTicked] = useState<ReadonlySet<string>>(() => new Set());

  const sections = rows.filter(
    (row): row is Extract<AutoChapterListRow, { kind: 'section' }> =>
      row.kind === 'section',
  );
  // S8 — every Section ticked is indistinguishable from All sections, so it is
  // composed as `all`: no special case, no second code path.
  const isSubset = choosing && ticked.size < sections.length;
  const untickedCount = sections.length - ticked.size;
  const nothingTicked = choosing && ticked.size === 0;

  // A subset run preserves rather than replaces, so there is nothing to warn
  // about (S5) — the Sections left unticked keep their chapters by definition.
  const replaces = existingChapterCount > 0 && !isSubset;
  // Only a specified count reaches the run: switching back to Auto leaves the
  // number typed in the field, and a stale number the creator can no longer see
  // must not silently steer the run.
  const parsedCount =
    countMode === 'auto' || count === '' ? undefined : Number(count);
  const isCountValid =
    countMode === 'auto' ||
    (parsedCount !== undefined &&
      Number.isInteger(parsedCount) &&
      parsedCount >= 1 &&
      parsedCount <= sectionCount);

  // Switching to Specify lands on the count the prompt would aim for anyway
  // (roughly one chapter per four Sections), so the field opens with a workable
  // number instead of an empty box that disables the action.
  const pickCountMode = (mode: 'auto' | 'specify') => {
    setCountMode(mode);
    if (mode === 'specify' && count === '') {
      setCount(
        String(
          Math.max(1, Math.min(sectionCount, Math.round(sectionCount / 4))),
        ),
      );
    }
  };

  const toggle = (id: string) =>
    setTicked(previous => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Modal isOpen={isOpen} closeModal={isRunning ? undefined : onClose}>
      <Card $size="lg" data-testid="auto-chapter-overlay">
        {/* The tip for "Vary the speakers". Rendered inside the card so it
            lives and dies with the dialog. */}
        <Tooltip id={AUTO_CHAPTER_TOOLTIP_ID} style={MODAL_TOOLTIP_STYLE} />
        <DialogHeader>
          <DialogTitle>Auto-Chapter this Reel</DialogTitle>
          {/* No longer promises the order will not change: with the reorder as
              the default (PLAT-11153 S1) that sentence was false on the run this
              dialog opens with, and what the order does is the Order field's to
              state — it says so under whichever segment is picked. */}
          <DialogSubtitle>
            The AI reads the whole reel, groups it into chapters and names each
            one.
          </DialogSubtitle>
        </DialogHeader>

        <Fields>
          <Field>
            <FieldLabel as="span">How many chapters</FieldLabel>
            <CountRow>
              <ToggleGroup
                ariaLabel="How many chapters"
                testIdPrefix="auto-chapter-count-mode"
                tone="accent"
                disabled={isRunning}
                value={countMode}
                onChange={pickCountMode}
                options={[
                  { id: 'auto', label: 'Auto' },
                  { id: 'specify', label: 'Specify' },
                ]}
              />
              {countMode === 'specify' && (
                <CountInput
                  id="auto-chapter-count"
                  type="number"
                  min={1}
                  max={sectionCount}
                  value={count}
                  disabled={isRunning}
                  aria-label="How many chapters"
                  data-testid="auto-chapter-count"
                  onChange={e => setCount(e.target.value)}
                />
              )}
            </CountRow>
            {/* The field reads "How many chapters", not "Exactly": the number is
                guidance to the reasoning and the client never enforces it
                (DS11), so the copy must not promise otherwise. */}
            <FieldHint>
              {countMode === 'auto'
                ? 'The AI decides where the reel turns.'
                : `A target to aim for, between 1 and ${sectionCount}.`}
            </FieldHint>
            {!isCountValid && (
              <FieldError data-testid="auto-chapter-count-error">
                Pick a number between 1 and {sectionCount}.
              </FieldError>
            )}
          </Field>

          {/* PLAT-11153 — one field, because the ordering and the grouping are
              one judgement (E1): deciding four Sections are one movement is
              deciding what order they play in. Not a second action. */}
          <Field>
            <FieldLabel as="span">Clip Order</FieldLabel>
            {/* Reordering reads first because it is the default and the bigger
                of the two answers — the segment on the left is the one the run
                will do unless the creator says otherwise. */}
            <OrderRow>
              <ToggleGroup
                ariaLabel="Clip Order"
                testIdPrefix="auto-chapter-order"
                tone="accent"
                disabled={isRunning}
                value={reorder ? 'reorder' : 'keep'}
                onChange={id => setReorder(id === 'reorder')}
                options={[
                  { id: 'reorder', label: 'Let the AI Reorder' },
                  { id: 'keep', label: 'Keep Current Order' },
                ]}
              />
              {/* Beside the choice it qualifies rather than under it, and gone
                  entirely while the order is being kept: with the sequence
                  fixed there is nothing to vary, so the control has no meaning
                  to hold open. The tick itself survives the detour (state, not
                  markup), so coming back to Let the AI Reorder finds it as it
                  was left. */}
              {reorder && (
                <SpeakerVariety>
                  <Switch
                    small
                    label="Vary the speakers"
                    checked={varySpeakers}
                    disabled={isRunning}
                    onClick={() => setVarySpeakers(!varySpeakers)}
                  />
                  {/* The explanation as an info tip rather than a paragraph:
                      it is a sentence about a switch, and set as body text it
                      outweighed the field it belongs to. Modal style — this
                      dialog is inside the clip-preview modal (CLAUDE.md). */}
                  <VarietyInfo
                    data-tooltip-id={AUTO_CHAPTER_TOOLTIP_ID}
                    data-tooltip-content="Favours an order that cuts between voices, so the reel plays as a conversation rather than one person at length."
                    aria-label="What varying the speakers does"
                  >
                    <InfoIconAlt width="13" height="13" />
                  </VarietyInfo>
                </SpeakerVariety>
              )}
            </OrderRow>
            <FieldHint>
              {reorder
                ? 'The AI decides the sequence as well as the grouping.'
                : 'The reel plays in the order it does now; the AI only groups and names.'}
            </FieldHint>
          </Field>

          {/* PLAT-11154 — which Sections are in play. */}
          <Field>
            <FieldLabel as="span">Clips</FieldLabel>
            <ToggleGroup
              ariaLabel="Clips"
              testIdPrefix="auto-chapter-sections"
              tone="accent"
              disabled={isRunning}
              value={choosing ? 'choose' : 'all'}
              onChange={id => setChoosing(id === 'choose')}
              options={[
                { id: 'all', label: 'All Clips' },
                { id: 'choose', label: 'Choose Clips' },
              ]}
            />

            {choosing && (
              <SectionList data-testid="auto-chapter-section-list">
                {rows.map(row =>
                  row.kind === 'chapter' ? (
                    // Not tickable: a chapter is a boundary, and the thing being
                    // chosen is which passages may move (S1).
                    <ChapterSeparator key={row.id}>
                      {row.title}
                    </ChapterSeparator>
                  ) : (
                    <SectionChoice
                      key={row.id}
                      type="button"
                      role="checkbox"
                      aria-checked={ticked.has(row.id)}
                      disabled={isRunning}
                      data-testid={`auto-chapter-section-${row.position}`}
                      onClick={() => toggle(row.id)}
                    >
                      {/* The tick box the picker and the speaker filter already
                          use, so the three controls around this panel read as
                          one set rather than as three near-identical boxes. */}
                      <ClipFilterCheckbox checked={ticked.has(row.id)} />
                      <SectionPosition>{row.position}</SectionPosition>
                      <SectionName>{row.name}</SectionName>
                    </SectionChoice>
                  ),
                )}
              </SectionList>
            )}

            {/* Stated before the run rather than discovered after it. */}
            {isSubset && (
              <FieldHint data-testid="auto-chapter-preserved-notice">
                The {untickedCount} {untickedCount === 1 ? 'clip' : 'clips'} you
                have not ticked keep their place and their chapter.
              </FieldHint>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="auto-chapter-guidance">
              Guidance (optional)
            </FieldLabel>
            <GuidanceInput
              id="auto-chapter-guidance"
              rows={3}
              value={guidance}
              placeholder="e.g. group by argument, not by speaker"
              disabled={isRunning}
              data-testid="auto-chapter-guidance"
              onChange={e => setGuidance(e.target.value)}
            />
          </Field>
        </Fields>

        {/* E8 — the creator is told what is destroyed *before* the run, in the
            same place the run is specified. A flat reel says nothing about
            replacement: there is nothing to replace, and this statement is about
            destroying work rather than about the generation.

            It says nothing about the reorder either, in either branch. That
            sentence ("This rearranges the reel. Undo restores the order but not
            the chapters.") sat under every run whose order was in play — which,
            since PLAT-11153 made the reorder the default, is almost all of them
            — and a notice that is always there is a notice nobody reads. What it
            said is also the thing the Order field two rows up already states, in
            the segment the creator picked. */}
        {replaces && (
          <ReplaceNotice data-testid="auto-chapter-replace-notice">
            This replaces the {existingChapterCount}{' '}
            {existingChapterCount === 1 ? 'chapter' : 'chapters'} you have now,
            and their names. It cannot be undone.
          </ReplaceNotice>
        )}

        {error && <RunError data-testid="auto-chapter-error">{error}</RunError>}

        <DialogFooter>
          {/* The run, said on the left of the footer with the dots that move:
              the primary button's label changes too, but a disabled button is a
              weak place to carry "this is working" — and the round trip here is
              the longest one in the panel. */}
          {isRunning && (
            <RunningNotice role="status" data-testid="auto-chapter-running">
              {/* Not "a name": this run groups the reel and names every chapter
                  it makes, so the label says what is being produced. */}
              <GeneratingNameLabel label="Generating chapter content" />
            </RunningNotice>
          )}
          <SecondaryButton
            type="button"
            disabled={isRunning}
            data-testid="auto-chapter-cancel"
            onClick={onClose}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="button"
            disabled={isRunning || !isCountValid || nothingTicked}
            // S8 — disabled with its reason rather than enabled and no-opping.
            title={
              nothingTicked ? 'Tick at least one clip to reorganize' : undefined
            }
            data-testid="auto-chapter-run"
            onClick={() =>
              onRun({
                chapterCount: parsedCount,
                reorder,
                // Never without the reorder that acts on it — the checkbox is
                // already disabled, and this is the same rule at the boundary.
                varySpeakers: reorder && varySpeakers,
                guidance: guidance.trim() || undefined,
                movableSectionIds: isSubset ? ticked : undefined,
              })
            }
          >
            {isRunning
              ? // The reel is about to look different, and the label should not
                // under-promise that (PLAT-11153).
                reorder
                ? 'Rebuilding the reel…'
                : 'Grouping the reel…'
              : isSubset
                ? // Nothing is destroyed, so nothing is replaced (S5).
                  'Update Chapters'
                : replaces
                  ? 'Replace Chapters'
                  : 'Create Chapters'}
          </PrimaryButton>
        </DialogFooter>
      </Card>
    </Modal>
  );
};

/**
 * The in-flight notice's place in the footer: hard left, with the two buttons
 * still on the right — `margin-right: auto` rather than a second flex rule, so
 * the shared `DialogFooter` keeps its one alignment.
 */
const RunningNotice = styled.div`
  margin-right: auto;
  display: inline-flex;
  align-items: center;
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
`;

/* The height every control row in this dialog holds, whatever is in it — the
   shared ToggleGroup's own rendered height (2×1px border + 2×2px track padding
   + a 3px-padded `--text-xs` segment). Pinned as a constant because the count
   field and the order field both have something that comes and goes, and a row
   that resizes under the pointer walks every field below it down the card. */
const CONTROL_ROW_HEIGHT = '26px';

/* Breathing room the card keeps above and below itself, whatever is in it. */
const VIEWPORT_MARGIN = '48px';

/* The dialog grows with the tick list until it reaches the viewport, and then
   scrolls its fields instead of running off the screen — the shape every
   scrolling modal in the app has (Clip Producer, the Add Clips picker): a fixed
   header, a fixed footer, and the body between them taking the overflow. The
   header and the actions therefore stay reachable no matter how long the reel
   is, which is the whole point: a Create Chapters button below the fold is a
   dialog with no way out but Escape. */
const Card = styled(DialogCard)`
  max-height: calc(100vh - 2 * ${VIEWPORT_MARGIN});
  overflow: hidden;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* The overflow lands here rather than on the card, so the title and the
     actions stay put. min-height: 0 because a flex child will not shrink
     below its content without it, which is what would push the footer off. */
  min-height: 0;
  overflow-y: auto;
  /* Never sideways. Setting overflow-y alone leaves overflow-x computed as
     auto, so anything that overruns the column earns a scrollbar across the
     dialog instead of being clipped. */
  overflow-x: hidden;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  /* Room for the scrollbar, and for a focus ring on the rightmost control. */
  padding-right: 2px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
`;

/* The order choice and the setting that qualifies it, on one line. Beside
   rather than beneath: it is a rule for *how* to reorder, so it belongs to the
   answer next to it, and a row keeps the field one line tall instead of the
   four the indented sub-block cost.

   min-height holds the line at the toggle's own height, so the switch
   appearing and disappearing with the segment cannot shift the fields below
   it. */
const OrderRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  flex-wrap: wrap;
  min-height: ${CONTROL_ROW_HEIGHT};
`;

const SpeakerVariety = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

/* The info tip's target. A button, not a bare icon, so it is reachable by
   keyboard and announced — the tip is the only place this setting is
   explained. */
const VarietyInfo = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: help;

  &:hover,
  &:focus-visible {
    color: var(--foreground);
  }
`;

/* The reel, in miniature. Scrolls inside itself rather than growing the dialog
   past the viewport: a twenty-Section reel is an ordinary one, and the card's
   own cap is the backstop rather than the first line of defence — a list that
   ate the whole dialog would push the Guidance field and the actions out of
   sight even though the card technically still fit.

   Sized in `vh` rather than at a fixed 220px so a tall screen shows more of the
   reel instead of a stub with a scrollbar. overscroll-behavior: contain keeps
   a flick inside the list from scrolling the dialog behind it once it bottoms
   out. */
const SectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 36vh;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  margin-top: 4px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  background-color: var(--input-background);
`;

/* A boundary, shown so the creator can see which chapter they are leaving
   alone. Not a control — it is the structure the choice happens inside. */
const ChapterSeparator = styled.div`
  margin: 6px 0 2px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);

  &:first-child {
    margin-top: 0;
  }
`;

/* A row in the tick list. A button rather than a `<label>` wrapping a native
   checkbox: the box is `ClipFilterCheckbox`, which draws the state and takes no
   input of its own, so the row itself has to be the control. `role="checkbox"`
   + `aria-checked` keeps it announced as one.

   Hover fills the row rather than the box — the same `--surface-elevated` row
   hover every list in the app uses (design-system.md → menu rows). */
const SectionChoice = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 4px 6px;
  border: none;
  border-radius: var(--radius-input);
  background: transparent;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  color: var(--foreground);
  text-align: left;
  cursor: pointer;
  transition: background-color var(--transition-fast);

  &:hover:not(:disabled) {
    background-color: var(--surface-elevated);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

/* Tabular so the numbers line up down the list rather than shuffling the names
   beside them. */
const SectionPosition = styled.span`
  min-width: 18px;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  text-align: right;
`;

const SectionName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* The toggle and, when it says Specify, the number beside it — one row, because
   the number is part of that answer rather than a decision under it. */
const CountRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: ${CONTROL_ROW_HEIGHT};
`;

const FieldHint = styled.span`
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

const FieldError = styled.span`
  font-size: var(--text-xs);
  color: var(--destructive);
`;

const CountInput = styled.input`
  width: 72px;
  height: ${CONTROL_ROW_HEIGHT};
  box-sizing: border-box;
  padding: 0 8px;
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  background-color: var(--input-background);
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-sm);

  &:focus {
    outline: none;
    border-color: var(--focus-border);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const GuidanceInput = styled.textarea`
  width: 100%;
  /* This app has no global border-box reset, so a 100%-wide field with padding
     and a border is 18px wider than its column — which is what put a horizontal
     scrollbar across the whole dialog. */
  box-sizing: border-box;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  background-color: var(--input-background);
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--focus-border);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

/* Stated, not shouted. It is the sentence the creator has to read before
   pressing a button that already says Replace, so it carries weight through
   position rather than through colour. */
const ReplaceNotice = styled.p`
  margin: 16px 0 0;
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
  color: var(--text-secondary);
`;

const RunError = styled.p`
  margin: 12px 0 0;
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
  color: var(--destructive);
`;
